import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Container, Box, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Divider, Button,
  useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import CelebrationIcon from '@mui/icons-material/Celebration';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LogoutIcon from '@mui/icons-material/Logout';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import BarChartIcon from '@mui/icons-material/BarChart';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import FeedbackFab from './FeedbackFab';
import InventoryIcon from '@mui/icons-material/Inventory';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InsightsIcon from '@mui/icons-material/Insights';
import SearchIcon from '@mui/icons-material/Search';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RuleIcon from '@mui/icons-material/Rule';
import { BRAND, PRODUCT, Product } from '../config';
import { signOut } from '../lib/auth';

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  path: string;
  matchPaths: string[];
  icon: React.ReactElement;
  adminOnly?: boolean;
  products: Product[];
}

const NAV_ITEMS: NavItem[] = [
  // Dienstplan
  { label: 'Dashboard', path: '/', matchPaths: ['/'], icon: <DashboardIcon />, products: ['dienstplan'] },
  { label: 'Personalplanung', path: '/planung', matchPaths: ['/planung', '/ergebnis'], icon: <AutoAwesomeIcon />, products: ['dienstplan'] },
  { label: 'Dienstpläne', path: '/dienstplaene', matchPaths: ['/dienstplaene'], icon: <CalendarMonthIcon />, products: ['dienstplan'] },
  { label: 'Mitarbeiter', path: '/mitarbeiter', matchPaths: ['/mitarbeiter'], icon: <PeopleIcon />, products: ['dienstplan'] },
  { label: 'Urlaub/Krank', path: '/urlaub', matchPaths: ['/urlaub'], icon: <BeachAccessIcon />, products: ['dienstplan'] },
  { label: 'Feiertage', path: '/feiertage', matchPaths: ['/feiertage'], icon: <CelebrationIcon />, products: ['dienstplan'] },
  { label: 'Kalibrierung', path: '/admin/kalibrierung', matchPaths: ['/admin/kalibrierung'], icon: <TuneIcon />, adminOnly: true, products: ['dienstplan'] },

  // Skonto
  { label: 'Suchen', path: '/suchen', matchPaths: ['/suchen'], icon: <SearchIcon />, products: ['skonto'] },
  { label: 'Dashboard', path: '/admin/dashboard', matchPaths: ['/admin/dashboard'], icon: <InsightsIcon />, adminOnly: true, products: ['skonto'] },
  { label: 'Rechnungen', path: '/rechnungen', matchPaths: ['/rechnungen'], icon: <ReceiptLongIcon />, adminOnly: true, products: ['skonto'] },
  { label: 'Feedback', path: '/admin/feedback', matchPaths: ['/admin/feedback'], icon: <FeedbackOutlinedIcon />, adminOnly: true, products: ['skonto'] },
  { label: 'Lieferanten', path: '/lieferanten', matchPaths: ['/lieferanten'], icon: <StorefrontIcon />, adminOnly: true, products: ['skonto'] },
  { label: 'Lieferanten-Prüfung', path: '/admin/lieferanten-pruefung', matchPaths: ['/admin/lieferanten-pruefung'], icon: <FactCheckIcon />, adminOnly: true, products: ['skonto'] },
  { label: 'Preis-Prüfung', path: '/admin/preis-pruefung', matchPaths: ['/admin/preis-pruefung'], icon: <RuleIcon />, adminOnly: true, products: ['skonto'] },
  { label: 'Artikel', path: '/artikel', matchPaths: ['/artikel'], icon: <InventoryIcon />, adminOnly: true, products: ['skonto'] },
];

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // noSsr: evaluate matchMedia on the FIRST render so the permanent desktop
  // sidebar never briefly mounts on a phone — that flash made the page ~240px
  // wider than the viewport, so mobile Safari zoomed out to fit.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const username = localStorage.getItem('username') || '';
  const userRole = localStorage.getItem('role') || 'planner';
  const isAdmin = userRole === 'admin';
  const visibleNavItems = NAV_ITEMS
    .filter(i => i.products.includes(PRODUCT))
    .filter(i => !i.adminOnly || isAdmin);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {PRODUCT === 'skonto'
          ? <ReceiptLongIcon color="secondary" />
          : <RestaurantIcon color="secondary" />
        }
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{BRAND.name}</Typography>
          <Typography variant="caption" color="text.secondary">{BRAND.subtitle}</Typography>
        </Box>
      </Box>
      <Divider />
      <List sx={{ flex: 1, pt: 1 }}>
        {visibleNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={item.matchPaths.some(p => location.pathname === p || (p !== '/' && location.pathname.startsWith(p)))}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{
                mx: 1, borderRadius: 2, mb: 0.5,
                '&.Mui-selected': { bgcolor: 'secondary.main', color: 'white',
                  '& .MuiListItemIcon-root': { color: 'white' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Angemeldet als <strong>{username}</strong>
        </Typography>
        <Button size="small" startIcon={<LogoutIcon />} onClick={handleLogout} fullWidth variant="outlined">
          Abmelden
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Desktop drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRight: '1px solid', borderColor: 'divider' },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main content — minWidth:0 lets this flex child shrink below its content's
          intrinsic width; overflowX hidden stops any wide child (tables) from
          widening the whole page on mobile. */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
        {isMobile && (
          <AppBar position="static" elevation={0} sx={{ bgcolor: 'primary.main' }}>
            <Toolbar>
              <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {visibleNavItems.find(i => i.matchPaths.some(p =>
                    location.pathname === p || (p !== '/' && location.pathname.startsWith(p))
                  ))?.label || BRAND.name}
                </Typography>
              </Box>
              <RestaurantIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
            </Toolbar>
          </AppBar>
        )}
        <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
          {children}
          <FeedbackFab />
        </Container>
      </Box>
    </Box>
  );
}
